import { AuthCardSkeleton } from "components/new-stuff/ui/Skeleton"
import { Alert, QRCode } from "antd"
import { LoadingRegion } from "components/custom/Loader"
import SegmentWrapper from "components/custom/SegmentWrapper"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { ReactNode, useEffect, useState } from "react"

import parser from "html-react-parser"
import { Button } from "components"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { authService, AUTH_ENDPOINTS } from "services/authService"
import { useLocation, useNavigate } from "react-router"
import { toast } from "react-toastify"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { AppOTPInput } from "components/inputs/AppOTPInput"
import { onQueryError } from "utils/utils"
import { useAuthStore } from "stores/auth"

interface I2FASetupInfo {
  secret_key: string
  barcode_url: string
}

interface I2FADetails {
  twoFactorEnabled: boolean
  twoFactorSetup: boolean | null
}

export default function TwoFaSetupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const state = useLocation().state as { setupToken?: string } | null
  const setupToken = state?.setupToken ?? ""

  const [otp, setOtp] = useState("")

  const { data: twoFaStatus, isLoading: gettingStatus } = useQuery<
    IBaseRes<I2FADetails>
  >({
    queryKey: [AUTH_ENDPOINTS.TWO_FA_DETAILS],
    queryFn: () => authService.get2FADetails(),
  })

  const isEnabled = twoFaStatus?.data?.twoFactorEnabled === true
  const alreadySetUp = twoFaStatus?.data?.twoFactorSetup === true

  // first-time setup reached without a setup token (direct nav / refresh) would ust render a broken QR — send them back to Profile to start over.
  //  An already-set-up account has no QR, so let it stay.

  useEffect(() => {
    if (!gettingStatus && !isEnabled && !alreadySetUp && !setupToken) {
      navigate(-1)
    }
  }, [gettingStatus, isEnabled, alreadySetUp, setupToken, navigate])

  const { data: setupInfo, isFetching: loadingSetupInfo } = useQuery<
    IBaseRes<I2FASetupInfo>
  >({
    queryKey: [AUTH_ENDPOINTS.SETUP_2FA_INFO, setupToken],
    queryFn: () => authService.setup2FAInfoWithToken(setupToken),
    // only fetch the QR for a genuine first-time setup
    enabled: !isEnabled && !!setupToken && !alreadySetUp,
    retry: false,
    onError: (err: any) => {
      onQueryError(err)
      navigate(-1)
    },
  })

  // the code is verified first; a successful verify swaps the setup token for a real session, then enables
  const { mutate: verify2FA, isLoading: verifying } = useMutation({
    mutationFn: () =>
      authService.verify2FACodeWithToken(
        { generated: false, code: otp },
        setupToken
      ),
    onSuccess: (data) => {
      const { access_token, refresh_token } = data?.data || {}
      useAuthStore.setState({
        authToken: access_token,
        refreshToken: refresh_token,
      })
      enable()
    },
    onError: (err: any) => {
      if (err?.response?.status === 401) {
        // expired setup token → back to Profile
        toast.error("Your request timed out — please try again")
        navigate(-1)
      } else {
        onQueryError(err)
      }
    },
  })

  const { mutate: enable, isLoading: enabling } = useMutation({
    mutationFn: () => authService.enable2FA({ code: otp }),
    onSuccess: (data) => {
      data?.message && toast.success(data.message)
      queryClient.invalidateQueries({
        queryKey: [AUTH_ENDPOINTS.TWO_FA_DETAILS],
      })
      navigate(-1)
    },
    onError: onQueryError,
  })

  const { mutate: disable, isLoading: disabling } = useMutation({
    mutationFn: () => authService.disable2FA({ code: otp }),
    onSuccess: (data: any) => {
      data?.message && toast.success(data.message)
      queryClient.invalidateQueries({
        queryKey: [AUTH_ENDPOINTS.TWO_FA_DETAILS],
      })
      navigate(-1)
    },
    onError: onQueryError,
  })

  const disabled = !otp || otp.length < 6

  return (
    <DashboardWrapper>
      <LoadingRegion
        placeholder={<AuthCardSkeleton />}
        spinning={gettingStatus || loadingSetupInfo}
      >
        <SegmentWrapper
          title="Two Factor Authentication"
          subtitle="Two factor authentication can keep the bad guys out even if they have your password."
        >
          {isEnabled || alreadySetUp ? (
            <div className="lg:px-[60px] md:px-6 px-4">
              <ContentRedender
                title="Verify Code"
                subtitle="Enter the code from your two-factor authenticator app."
                content={
                  <AppOTPInput
                    otp={otp}
                    setOtp={setOtp}
                  />
                }
              />
            </div>
          ) : (
            <div className="lg:px-[60px] md:px-6 px-4">
              <Alert
                type="info"
                showIcon
                message="Go through the following steps to setup this app in your authenticator app"
                className="w-[100%] lg:w-[60%]"
                icon={<InformationCircleIcon className="w-4 h-4" />}
              />
              <div className="flex flex-col gap-6 mt-5 mb-8">
                <ContentRedender
                  title="1. Download the Authenticator app"
                  subtitle="Download a two-factor authenticator app like Microsoft Authenticator or Google Authenticator."
                />
                <ContentRedender
                  title="2. Scan QR Code"
                  subtitle={
                    parser(`Scan the QR Code or enter this key <strong>
                ${setupInfo?.data?.secret_key}
                </strong> into your two-factor authenticator app.`) as string
                  }
                  content={
                    <QRCode
                      value={`${setupInfo?.data?.barcode_url}`}
                      bordered={false}
                      type="svg"
                      size={250}
                    />
                  }
                />
                <ContentRedender
                  title="3. Verify Code"
                  subtitle="Once you have scanned the QR Code or entered the key, your two-factor authentication app will provide you with a 6-digit unique code. Enter the code to activate two-factor authentication."
                  content={
                    <AppOTPInput
                      otp={otp}
                      setOtp={setOtp}
                    />
                  }
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6 border-t pr-6 pt-6 pb-4 w-full bg-white">
            <Button
              color="secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>

            {isEnabled ? (
              <Button
                color="primary"
                type="submit"
                submitting={disabling}
                onClick={() => disable()}
                disabled={disabled}
              >
                Disable
              </Button>
            ) : (
              <Button
                color="primary"
                type="submit"
                submitting={enabling || verifying}
                onClick={() => verify2FA()}
                disabled={disabled}
              >
                Enable
              </Button>
            )}
          </div>
        </SegmentWrapper>
      </LoadingRegion>
    </DashboardWrapper>
  )
}

const ContentRedender = ({
  title,
  content,
  subtitle,
}: {
  title: string
  subtitle: string
  content?: ReactNode
}) => {
  return (
    <div>
      <div className="text-[16px] font-[600] ">{title}</div>
      <div className="text-[14px] font-[400] text=[#42526D] pr-12">
        {subtitle}
      </div>
      {content && <div className="my-2">{content}</div>}
    </div>
  )
}
